/** @jsx isolateComponent */
import { ServiceIconShell } from '../ServiceIconShell';
import { isolateComponent } from '@zapier/style-encapsulation';
import { Colors, Img } from '@zapier/design-system';
import type { ShadowValue } from '@zapier/design-system';

export type ServiceIconSize = 20 | 30 | 40 | 60 | 80 | 120 | 150;
type ColorName = keyof typeof Colors;
type ServiceIconProps = {
  /**
   * Inform screen-readers that they should ignore the image. Image will still be visually displayed.
   *
   * @default false
   */
  ariaHidden?: boolean;
  /**
   * @deprecated - Previously used by `Img` for the `react-lazyload` library.
   * We are now using a native browser-level lazyload so this no longer does
   * anything.
   *
   * Indicates whether the icon should lazily load. If `undefined`
   * then it'll default to `Img`'s `lazyloadOffset`.
   */
  lazyloadOffset?: number | number[] | null;
  /**
   * @deprecated - Previously used by `Img` for the `react-lazyload` library.
   * We are now using a native browser-level lazyload so this no longer does
   * anything.
   *
   * Will properly load content that is initially overflowed outside of parent
   * container (see caveats in docs: https://www.npmjs.com/package/react-lazyload#overflow)
   */
  lazyloadOverflow?: boolean;
  /**
   * The name of the service whose icon is being displayed (used as alt text for the icon), e.g., "Google Sheets".
   */
  serviceName: string;
  /**
   * A URL for the service icon image to be rendered. If this is an Imgix URL, a large-format image can be used and the image will automatically be resized.
   */
  src: string;
  /**
   * Sets the width and height of the icon (since it is a square), in pixels.
   * The list of possible sizes is finite to enforce consistency across our UI.
   */
  size: ServiceIconSize;
  /**
   * Whether to render the ServiceIcon as part of a grouping of Icons.
   *
   * @default false
   */
  isInGroup?: boolean;
  /**
   * Applies a shadow to the ServiceIcon.
   */
  shadow?: ShadowValue;
  /**
   * Applies a border color to the ServiceIconShell.
   *
   * @default 'grey200'
   */
  borderColor?: ColorName;
  /**
   * Applies a border size to the ServiceIconShell.
   *
   * @default 1
   */
  borderWidth?: number;
  /**
   * Applies a custom alt attribute to the underlying Img component.
   */
  alt?: string;
};

const getPropsWithDefaults = (props: ServiceIconProps) => ({
  ariaHidden: false,
  borderColor: 'grey200' as ColorName,
  borderWidth: 1,
  isInGroup: false,
  ...props,
});

/**
 * Renders an icon representing a service (aka app, aka integration).
 */
export const ServiceIcon = (_props: ServiceIconProps) => {
  const props = getPropsWithDefaults(_props);
  const {
    ariaHidden,
    borderColor,
    borderWidth,
    serviceName,
    size,
    src,
    shadow,
    isInGroup,
    alt,
  } = props;
  const iconSize = Math.ceil(0.7 * size);
  return (
    <ServiceIconShell
      borderColor={borderColor}
      borderWidth={borderWidth}
      height={size}
      isInGroup={isInGroup}
      shadow={shadow}
      width={size}
    >
      <Img
        alt={alt || `${serviceName} logo`}
        ariaHidden={ariaHidden}
        hasRadius={false}
        height={iconSize}
        src={src}
        width={iconSize}
      />
    </ServiceIconShell>
  );
};
